import React, { useEffect, useState, useMemo, useCallback } from "react"
import { endpointGetExchangeRates, endpointSetExchangeRate, endpointDeleteExchangeRate, urlInk } from "../services/Endpoints";
import { Loading } from "./Loading";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencil, faTrash, faSync } from '@fortawesome/free-solid-svg-icons';
import Paginator from "./Paginator";
import ExchangeRateModal from "./ExchangeRateModal";
import ConfirmationModal from "./ConfirmationModal";
import AuthService from "../services/AuthService";
import { theme } from "../theme";

export function ExchangeRates({ filters, refreshKey }) {

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rates, setRates] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRate, setSelectedRate] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [rateToDelete, setRateToDelete] = useState(null);
    const itemsPerPage = 10;

    const getRates = useCallback(async () => {
        try {
            setLoading(true);
            const token = AuthService.getToken();
            const params = new URLSearchParams({
                type: filters.type || '',
                period: filters.period || '',
            });
            const response = await fetch(`${urlInk}${endpointGetExchangeRates}?${params}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": token
                }
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setRates(res.data);
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching exchange rates:", error);
            setLoading(false);
        }
    }, [filters.type, filters.period]);

    useEffect(() => {
        getRates();
        return () => {
            setRates(() => [])
        }
    }, [refreshKey, getRates]);

    const handleAdd = () => {
        setSelectedRate(null);
        setIsModalOpen(true);
    }

    const handleEdit = (rate) => {
        setSelectedRate(rate);
        setIsModalOpen(true);
    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRate(null);
    }

    const handleSave = async (form) => {
        try {
            setSaving(true);
            const token = AuthService.getToken();
            const payload = {
                type: form.type,
                period: form.period,
                value: form.value,
            };
            if (form.type === 'INGRAM') {
                payload.currency = form.currency;
            }
            if (selectedRate) {
                payload.id = selectedRate.id;
            }

            const response = await fetch(`${urlInk}${endpointSetExchangeRate}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": token
                },
                body: JSON.stringify(payload)
            });
            const res = await response.json();
            setSaving(false);
            if (res && res.status === 'success') {
                setIsModalOpen(false);
                setSelectedRate(null);
                await getRates();
                return { ok: true };
            }
            return { ok: false, msg: res.msg || 'Error al guardar el tipo de cambio' };
        } catch (error) {
            console.error("Error saving exchange rate:", error);
            setSaving(false);
            return { ok: false, msg: 'Error al guardar el tipo de cambio' };
        }
    }

    const handleRequestDelete = (rate) => {
        setRateToDelete(rate);
        setIsDeleteModalOpen(true);
    }

    const handleConfirmDelete = async () => {
        try {
            const token = AuthService.getToken();
            const response = await fetch(`${urlInk}${endpointDeleteExchangeRate}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": token
                },
                body: JSON.stringify({
                    type: rateToDelete.type,
                    id: rateToDelete.id
                })
            });
            const res = await response.json();
            setIsDeleteModalOpen(false);
            setRateToDelete(null);
            if (res && res.status === 'success') {
                await getRates();
            }
        } catch (error) {
            console.error("Error deleting exchange rate:", error);
            setIsDeleteModalOpen(false);
            setRateToDelete(null);
        }
    }

    const paginatedRates = useMemo(() => {
        const offset = (currentPage - 1) * itemsPerPage;
        return rates.slice(offset, offset + itemsPerPage);
    }, [rates, currentPage]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const typeColors = {
        USD_MXN: theme.primary,
        INGRAM: theme.success
    };

    return (
        <>
            <div className="row mb-3 mt-4">
                <div className="col-md-12 d-flex justify-content-between align-items-center">
                    <span className="fw-bold small text-uppercase text-white opacity-75">
                        {rates.length} tipo(s) de cambio
                    </span>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-ink-icon"
                            onClick={() => getRates()}
                            title="Recargar lista"
                        >
                            <FontAwesomeIcon icon={faSync} spin={loading} />
                        </button>
                        <button
                            className="btn d-flex align-items-center gap-2"
                            onClick={handleAdd}
                            style={{
                                background: theme.primary,
                                color: '#fff',
                                border: 'none',
                                borderRadius: '10px',
                                padding: '8px 18px',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                boxShadow: `0 2px 12px rgba(${theme.primaryRgb},0.35)`,
                                transition: 'transform 0.15s, box-shadow 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 18px rgba(${theme.primaryRgb},0.5)`; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 2px 12px rgba(${theme.primaryRgb},0.35)`; }}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Agregar
                        </button>
                    </div>
                </div>
            </div>

            {loading === true &&
                <Loading />
            }

            <div className="modern-table-container">
                <table className="modern-table striped-table">
                    <thead>
                        <tr>
                            <th>Tipo</th>
                            <th>Periodo</th>
                            <th>Moneda</th>
                            <th>Valor</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRates.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center py-5 text-white-50">
                                    <h6 className="m-0 opacity-50">No se encontraron resultados para los filtros aplicados</h6>
                                </td>
                            </tr>
                        ) : (
                            paginatedRates.map((rate) => (
                                <tr key={`${rate.type}-${rate.id}`}>
                                    <td className="short">
                                        <span
                                            className="badge-status"
                                            style={{
                                                background: `${typeColors[rate.type] || theme.primary}22`,
                                                color: typeColors[rate.type] || theme.primaryHover,
                                                border: `1px solid ${typeColors[rate.type] || theme.primary}66`,
                                            }}
                                        >
                                            {rate.type}
                                        </span>
                                    </td>
                                    <td className="short">{rate.period}</td>
                                    <td className="short">
                                        {rate.type === 'USD_MXN' ? (
                                            <span className="text-white-50">USD → MXN</span>
                                        ) : (
                                            (rate.currency || 'GBP') + ' → USD'
                                        )}
                                    </td>
                                    <td className="short fw-bold text-white">{rate.value}</td>
                                    <td>
                                        <div className="d-flex gap-3">
                                            <div onClick={() => handleEdit(rate)} className="action cursor-pointer text-white-50" title="Editar">
                                                <FontAwesomeIcon icon={faPencil} />
                                            </div>
                                            <div onClick={() => handleRequestDelete(rate)} className="action cursor-pointer text-danger" title="Eliminar">
                                                <FontAwesomeIcon icon={faTrash} />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {rates.length > itemsPerPage && (
                <Paginator
                    totalItems={rates.length}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                />
            )}

            <ExchangeRateModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                rate={selectedRate}
                onSave={handleSave}
                saving={saving}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Eliminar tipo de cambio"
                message={`¿Estás seguro de eliminar el tipo de cambio ${rateToDelete ? rateToDelete.type : ''} del periodo ${rateToDelete ? rateToDelete.period : ''}?`}
                confirmText="Eliminar"
                cancelText="Cancelar"
                type="danger"
            />
        </>
    )
}

export default ExchangeRates
